// DeliveryChannels.jsx
import React from 'react';
import {
    Box,
    Flex,
    Text,
    VStack,
    HStack,
    IconButton,
    Grid
} from '@chakra-ui/react';
import {
    DndContext,
    closestCenter,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
} from '@dnd-kit/core';
import {
    SortableContext,
    sortableKeyboardCoordinates,
    useSortable,
    verticalListSortingStrategy,
    arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { FiX, FiMenu } from 'react-icons/fi';
import { FaMessage } from 'react-icons/fa6';

// ── Sortable Row ──────────────────────────────────────────────────────────────
function SortableChannelItem({ channelId, index, channel, onRemove }) {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
        useSortable({ id: channelId });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
        zIndex: isDragging ? 10 : 'auto',
    };

    return (
        <Flex
            ref={setNodeRef}
            style={style}
            align="center"
            gap={3}
            p={3}
            bg="white"
            border="1px solid"
            borderColor="gray.200"
            borderRadius="8.25px"
            _hover={{ borderColor: 'var(--brand-primary)', shadow: 'sm' }}
            transition="all 0.2s"
        >
            {/* Drag Handle */}
            <Box
                {...attributes}
                {...listeners}
                cursor="grab"
                color="gray.400"
                _hover={{ color: 'gray.600' }}
                display="flex"
                alignItems="center"
            >
                <FiMenu size={16} />
            </Box>

            {/* Priority Badge */}
            <Flex
                w="24px" h="24px" borderRadius="full"
                bg="var(--brand-primary)" color="white"
                align="center" justify="center"
                fontSize="xs" fontWeight="bold"
                flexShrink={0}
            >
                {index + 1}
            </Flex>

            {/* Channel Name */}
            <Text fontSize="sm" fontWeight="medium" flex={1}>
                {channel?.displayName || channel?.name || channelId}
            </Text>

            {/* Remove */}
            <IconButton
                icon={<FiX />}
                size="xs"
                variant="ghost"
                colorScheme="red"
                aria-label="Remove channel"
                onClick={() => onRemove(channelId)}
            />
        </Flex>
    );
}

// ── Channel Selector ──────────────────────────────────────────────────────────
function ChannelSelector({ channels, selectedIds, onToggle }) {
    return (
        <Grid templateColumns="repeat(2, 1fr)" gap={3} mb={4}>
            {channels.map(ch => {
                const isSelected = selectedIds.includes(ch.id);
                return (
                    <Box
                        key={ch.id}
                        border="1px solid"
                        borderColor={isSelected ? 'var(--brand-primary)' : '#e2e8f0'}
                        bg={isSelected ? 'blue.50' : 'white'}
                        cursor="pointer"
                        borderRadius="8.25px"
                        p={3}
                        onClick={() => onToggle(ch.id)}
                        _hover={{ borderColor: 'var(--brand-primary)', shadow: 'sm' }}
                        transition="all 0.2s"
                    >
                        <HStack justify="space-between">
                            <Text fontSize="sm" fontWeight="medium" color="gray.800">
                                {ch.displayName || ch.name}
                            </Text>
                            <Box
                                w="16px" h="16px" borderRadius="3px"
                                border="2px solid"
                                borderColor={isSelected ? 'var(--brand-primary)' : 'gray.300'}
                                bg={isSelected ? 'var(--brand-primary)' : 'white'}
                                display="flex" alignItems="center" justifyContent="center"
                                flexShrink={0}
                            >
                                {isSelected && (
                                    <Box w="8px" h="8px" bg="white" borderRadius="1px"
                                        style={{ clipPath: 'polygon(14% 44%, 0 65%, 50% 100%, 100% 16%, 80% 0%, 43% 62%)' }}
                                    />
                                )}
                            </Box>
                        </HStack>
                    </Box>
                );
            })}
        </Grid>
    );
}

// ── Main Component ────────────────────────────────────────────────────────────
export function DeliveryChannels({ channels = [], selectedChannels, setSelectedChannels }) {
    const sensors = useSensors(
        useSensor(PointerSensor),
        useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
    );

    const handleToggle = (channelId) => {
        setSelectedChannels(prev => {
            if (prev.includes(channelId)) return prev.filter(id => id !== channelId);
            return [...prev, channelId];
        });
    };

    const handleRemove = (channelId) => {
        setSelectedChannels(prev => prev.filter(id => id !== channelId));
    };

    const handleDragEnd = ({ active, over }) => {
        if (!over || active.id === over.id) return;
        setSelectedChannels(prev => {
            const oldIdx = prev.indexOf(active.id);
            const newIdx = prev.indexOf(over.id);
            return arrayMove(prev, oldIdx, newIdx);
        });
    };

    return (
        <Box>
            {/* Channel Picker */}
            <Text fontSize="sm" fontWeight="medium" color="gray.700" mb={2}>
                Select Channels
            </Text>
            <ChannelSelector
                channels={channels}
                selectedIds={selectedChannels}
                onToggle={handleToggle}
            />

            {/* Sortable Priority List */}
            <Box
                border="1px solid"
                borderColor="#0000001a"
                p={4}
                borderRadius="8.25px"
                bg="#f1f5f980"
            >
                <Flex justify="space-between" align="center" mb={4}>
                    <Text fontSize="md" fontWeight="semibold">Delivery Channels</Text>
                    <Text fontSize="xs" color="gray.400">Drag to reorder priority</Text>
                </Flex>

                {selectedChannels.length > 0 ? (
                    <DndContext
                        sensors={sensors}
                        collisionDetection={closestCenter}
                        onDragEnd={handleDragEnd}
                    >
                        <SortableContext
                            items={selectedChannels}
                            strategy={verticalListSortingStrategy}
                        >
                            <VStack spacing={2} align="stretch">
                                {selectedChannels.map((id, idx) => (
                                    <SortableChannelItem
                                        key={id}
                                        channelId={id}
                                        index={idx}
                                        channel={channels.find(c => c.id == id)}
                                        onRemove={handleRemove}
                                    />
                                ))}
                            </VStack>
                        </SortableContext>
                    </DndContext>
                ) : (
                    <Flex
                        justify="center" align="center" direction="column"
                        border="3px dashed" borderColor="#ebe6e7" borderRadius="8.25px" p={8}
                    >
                        <FaMessage color="#d1d5dc" fontSize="24px" />
                        <Text color="black" fontSize="sm" fontWeight="semibold" mt={2}>No channel selected</Text>
                        <Text fontSize="xs" color="#6a7282">Please select at least one channel above</Text>
                    </Flex>
                )}

                {selectedChannels.length > 0 && (
                    <Text fontSize="xs" color="gray.500" mt={3}>
                        * If a channel fails, the message will automatically fall back to the next channel in order
                    </Text>
                )}
            </Box>
        </Box>
    );
}